import { initiateTwitterLogin, initiateTwitterLogout } from "@/api/twitter.ts";
import { useToast } from "@/hooks/use-toast.ts";
import { Button } from "@/components/ui/button.tsx";
import { useQueryClient } from "@tanstack/react-query";
import type { Persona } from "@/api/personas";
import { useAuth } from "@/hooks/use-auth.tsx";
import { Role } from "@/components/AuthorizationProvider.tsx";

export function TwitterConnectionStatus({
  persona,
  twitterHandle,
  isConnected,
}: {
  persona: Persona;
  twitterHandle?: string;
  isConnected: boolean;
}) {
  const { toast } = useToast();
  const { hasAtLeastRole } = useAuth();
  const queryClient = useQueryClient();

  const isAdmin = hasAtLeastRole(Role.TenantAdmin);

  const editPersonaDisabled =
    !persona.persona_id || (persona.public_persona === true && !isAdmin);

  const handleConnectTwitter = async (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    try {
      e.preventDefault();
      const redirectUrl = encodeURIComponent(window.location.href);
      window.location.href = await initiateTwitterLogin(
        persona.persona_id,
        redirectUrl,
      );
    } catch (error) {
      console.error("Error initiating Twitter login:", error);
      toast({
        description: "Error connecting to Twitter. Please try again later.",
      });
    }
  };

  const handleLogoutTwitter = async (e: any) => {
    try {
      e.preventDefault();
      await initiateTwitterLogout(persona.persona_id);
      toast({ description: "Your X account has been disconnected." });
      await queryClient.invalidateQueries({ queryKey: ["personas"] });
    } catch (error) {
      console.error("Error initiating Twitter logout:", error);
      toast({
        description: "Error connecting to Twitter. Please try again later.",
      });
    }
  };

  return (
    <div className="w-full flex flex-col items-center">
      {isConnected && (
        <span className="text-sm">
          Connected as <strong>@{twitterHandle}</strong>
        </span>
      )}

      {!isConnected ? (
        <Button
          className="my-2"
          variant="outline"
          onClick={handleConnectTwitter}
        >
          Connect Twitter
        </Button>
      ) : (
        !editPersonaDisabled && (
          <Button
            className="my-2"
            variant="outline"
            onClick={handleLogoutTwitter}
          >
            Logout Twitter
          </Button>
        )
      )}
    </div>
  );
}
